"use client";

import * as React from "react";

// Next.js App Router root error boundary. This is the boundary that renders
// when a client render error (e.g. React #185 "Maximum update depth exceeded")
// bubbles out of the layout/page and is caught by the framework — i.e. the
// "red box" users see. Earlier capture attempts missed it: a window "error"
// listener only sees *uncaught* errors, and a boundary around the launcher only
// sees errors *under* it. This fires for the framework-caught error itself, so
// it finally lands in our logs. With the production source maps, error.stack
// de-minifies to the exact looping component.
function reportToServer(kind: string, error: Error & { digest?: string }) {
  try {
    const body = JSON.stringify({
      kind,
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      url: typeof window !== "undefined" ? window.location.href : null,
      reportedAt: new Date().toISOString(),
    });

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon("/api/client-error", new Blob([body], { type: "application/json" }));
      return;
    }

    void fetch("/api/client-error", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    });
  } catch {
    // Never let error reporting throw.
  }
}

export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  React.useEffect(() => {
    reportToServer("global-error", error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          fontFamily: "system-ui, -apple-system, sans-serif",
          display: "flex",
          minHeight: "100vh",
          alignItems: "center",
          justifyContent: "center",
          margin: 0,
        }}
      >
        <div style={{ maxWidth: 420, textAlign: "center", padding: 24 }}>
          <h2 style={{ fontSize: 18, marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: "#64748b", marginBottom: 16 }}>
            The page hit an unexpected error. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              borderRadius: 10,
              border: "1px solid #cbd5e1",
              background: "#fff",
              padding: "8px 16px",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      </body>
    </html>
  );
}
